import { Box } from '@mui/material';
import {
    DescriptionOutlined,
    LogoutOutlined,
    MoveToInboxOutlined,
    PaymentsOutlined,
    PeopleAltOutlined,
    QuestionAnswerOutlined,
    StorefrontOutlined,
} from '@mui/icons-material';
import { DocumentStatus, FeatureFlag, UserRole } from '@synergy/models';
import { PaloChrome, PaloNavSubItem, PaloSection, paloInitials, paloSignOutHref } from '@synergy/components-palo';
import { PropsWithChildren, ReactElement, useEffect } from 'react';
import { SHOP_URL } from '@synergy/config';
import { hasFeature, hasRole } from '@synergy/utils';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from './features/sidebar-menu/navigation-drawer/routes';
import { changeAccount } from './common/actions/account-actions';
import { shopLink, useShopCartCount } from './common/components/shop-link';
import { usePendingApprovalCount } from './features/order-approvals/use-pending-approval-count';
import { useSelector } from './store/use-appstate-selector';
import { useThunkDispatch } from './store/use-thunk-dispatch';
import paloLogo from './common/style/palo-logo.svg';

/**
 * Page size, page and sort, the way the drawer's links carried them.
 *
 * The lists apply their defaults on reset, not on first load, so a link that
 * drops these hands the backend an empty query and the list comes back in a
 * different order than the one people know. Contacts sort by name, everything
 * else by document number, newest first.
 */
const listUrl = (base: string): string => `${base}?limit=50&page=1&sort=number&order=desc`;
const contactsUrl = (base: string): string => `${base}?limit=50&page=1&sort=lastName&order=asc`;

/**
 * The PALO shell for the customer portal.
 *
 * Takes its children rather than an Outlet, because this app still routes with
 * a plain <Routes> block inside App rather than a data router.
 *
 * Two of the drawer's entries lead into the shop, which is a separate
 * application on its own host — they stay in the rail so the way there is not
 * lost, and leave through the browser rather than the router. The orders
 * moved here from the shop (#2279).
 */
/** Query parameter a mail link carries to open in the company it was written for. */
const ACCOUNT_PARAM = 'account';

export const PaloAppLayout = ({ children }: PropsWithChildren): ReactElement => {
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector(state => state.user);
    const fullName = useSelector(state => state.user?.fullName ?? '');
    const isImpersonator = useSelector(state => !!state.settings?.isImpersonator);
    const account = useSelector(state => state.account);
    const accounts = useSelector(state => state.accounts);
    const dispatch = useThunkDispatch();

    // A contact may look after several member companies, and every page —
    // tasks, reports, counters — speaks for exactly one of them. Which one was
    // readable only after opening a menu behind an unlabelled icon, so the
    // company now stands in the bar and the others are one click away
    // (#2215, R-71).
    const otherAccounts = accounts.filter(other => other.id !== account?.id);

    const switchAccount = (accountId: number): void => {
        void dispatch(changeAccount(accountId)).then(() => window.location.reload());
    };

    /**
     * A mail that was written for one company opens in that company.
     *
     * The invitations go out per company, so a contact who looks after two of
     * them receives two of them; without this both links landed in whichever
     * company the session happened to hold (#2215, R-64).
     */
    useEffect(() => {
        const wanted = Number(new URLSearchParams(location.search).get(ACCOUNT_PARAM));

        if (!wanted || !accounts.some(other => other.id === wanted)) {
            return;
        }

        const url = new URL(window.location.href);
        url.searchParams.delete(ACCOUNT_PARAM);

        if (wanted === account?.id) {
            window.history.replaceState(null, '', url.toString());

            return;
        }

        void dispatch(changeAccount(wanted)).then(() => window.location.replace(url.toString()));
    }, [account?.id, accounts, dispatch, location.search]);

    const t = (id: string): string => intl.formatMessage({ id });
    const cartCount = useShopCartCount();
    const pendingApprovals = usePendingApprovalCount();
    const toWebshop = intl.formatMessage({ id: 'app.navigation.toWebshop' }, { count: cartCount });
    const isShopAdmin = hasRole(UserRole.ShopAdmin, user);

    const companyItems: PaloNavSubItem[] = [
        ...(isShopAdmin
            ? [
                  { label: t('app.navigation.contactsLogins'), to: contactsUrl(ROUTES.userContacts) },
                  { label: t('app.navigation.notifications'), to: ROUTES.notifications },
              ]
            : []),
    ];

    // The people list narrowed the ways the template names them (#2283).
    const peopleFilters: PaloNavSubItem[] = isShopAdmin
        ? [
              { label: t('app.contacts.limits.filter.withLogin'), param: 'userStatus=USER_STATUS_SHOP_USER' },
              { label: t('app.contacts.limits.filter.withoutLogin'), param: 'userStatus=USER_STATUS_NO_USER' },
              { label: t('app.contacts.limits.filter.admins'), param: 'userStatus=USER_STATUS_SHOP_ADMIN' },
              { label: t('app.contacts.limits.filter.nearLimit'), param: 'limitStatus=NEAR_LIMIT' },
              { label: t('app.contacts.limits.filter.approvalAlways'), param: 'limitStatus=APPROVAL_ALWAYS' },
          ].map(({ label, param }) => ({ label, to: `${contactsUrl(ROUTES.userContacts)}&${param}` }))
        : [];

    const sections: PaloSection[] = [
        {
            key: 'demands',
            label: t('app.navigation.demands'),
            icon: <DescriptionOutlined />,
            to: listUrl(ROUTES.demands.overview),
            navTitle: t('app.navigation.demands'),
            groups: [
                {
                    label: t('app.navigation.group.overview'),
                    items: [
                        { label: t('app.navigation.demands.overview'), to: listUrl(ROUTES.demands.overview) },
                        ...(isShopAdmin ? [{ label: t('app.navigation.demands.topics'), to: ROUTES.topics }] : []),
                    ],
                },
            ],
        },
        {
            key: 'inquiries',
            label: t('app.navigation.inquiries'),
            icon: <QuestionAnswerOutlined />,
            to: listUrl(ROUTES.inquiries),
            navTitle: t('app.navigation.inquiries'),
            groups: [
                {
                    label: t('app.navigation.group.overview'),
                    items: [{ label: t('app.filter.all'), to: listUrl(ROUTES.inquiries) }],
                },
            ],
        },
        ...(hasRole([UserRole.ShopAdmin, UserRole.CustomerBonusContact], user) &&
        hasFeature(FeatureFlag.BonusSettlements)
            ? [
                  {
                      key: 'bonusSettlements',
                      label: t('app.navigation.bonusSettlements.overview'),
                      icon: <PaymentsOutlined />,
                      to: ROUTES.bonusSettlements.overview,
                      navTitle: t('app.navigation.bonusSettlements.overview'),
                      groups: [
                          {
                              label: t('app.navigation.group.overview'),
                              items: [{ label: t('app.filter.all'), to: ROUTES.bonusSettlements.overview }],
                          },
                      ],
                  },
              ]
            : []),
        {
            key: 'purchases',
            label: t('app.navigation.purchases'),
            icon: <MoveToInboxOutlined />,
            to: listUrl(ROUTES.orders),
            navTitle: t('app.navigation.purchases'),
            groups: [
                {
                    label: t('app.navigation.group.overview'),
                    items: [
                        { label: t('app.filter.all'), to: listUrl(ROUTES.orders) },
                        ...(isShopAdmin && user?.contact
                            ? [
                                  {
                                      label: t('app.orders.filter.mine'),
                                      to: `${listUrl(ROUTES.orders)}&customerContactId=${user.contact.id}`,
                                  },
                              ]
                            : []),
                        {
                            label: t('app.orders.filter.cancelled'),
                            to: `${listUrl(ROUTES.orders)}&statuses=${DocumentStatus.Canceled}`,
                        },
                        {
                            label: t('app.navigation.orderApproval'),
                            to: ROUTES.orderApprovals,
                            count: pendingApprovals || undefined,
                            // Red asks for a decision, which only the shop admin makes.
                            alert: isShopAdmin && pendingApprovals > 0,
                        },
                        { label: t('app.navigation.buyAgain'), to: `${ROUTES.buyAgain}?sort=frequent&order=desc` },
                        {
                            label: t('app.buyAgain.filter.recent'),
                            to: `${ROUTES.buyAgain}?sort=recent&order=desc`,
                        },
                        ...(isShopAdmin && user?.contact
                            ? [
                                  {
                                      label: t('app.buyAgain.filter.mine'),
                                      to: `${ROUTES.buyAgain}?sort=frequent&order=desc&customerContactId=${user.contact.id}`,
                                  },
                              ]
                            : []),
                    ],
                },
            ],
        },
        // Only the shop admin keeps the company's settings; the order limits
        // that stood here for everyone went with #2282.
        ...(companyItems.length > 0
            ? [
                  {
                      key: 'company',
                      label: t('app.navigation.myCompany'),
                      icon: <PeopleAltOutlined />,
                      to: companyItems[0].to,
                      navTitle: t('app.navigation.myCompany'),
                      groups: [
                          { label: t('app.navigation.group.settings'), items: companyItems },
                          ...(peopleFilters.length > 0
                              ? [{ label: t('app.contacts.limits.filter'), items: peopleFilters }]
                              : []),
                      ],
                  },
              ]
            : []),
        {
            key: 'shop',
            label: shopLink(toWebshop),
            icon: <StorefrontOutlined />,
            to: SHOP_URL,
            navTitle: toWebshop,
            groups: [],
        },
    ];

    /** Pathname prefixes that map a route onto its rail section. */
    const sectionMatchers: { key: string; prefixes: string[] }[] = [
        { key: 'demands', prefixes: [ROUTES.demands.overview, ROUTES.topics] },
        { key: 'inquiries', prefixes: [ROUTES.inquiries] },
        { key: 'purchases', prefixes: [ROUTES.orders, ROUTES.orderApprovals, ROUTES.buyAgain] },
        { key: 'bonusSettlements', prefixes: [ROUTES.bonusSettlements.overview] },
        { key: 'company', prefixes: [ROUTES.notifications, ROUTES.userContacts] },
    ];

    const activeSectionKey =
        sectionMatchers.find(matcher =>
            matcher.prefixes.some(prefix => location.pathname === prefix || location.pathname.startsWith(prefix + '/'))
        )?.key ?? 'demands';

    const handleNavigate = (to: string): void => {
        if (/^https?:\/\//.test(to)) {
            window.location.assign(to);

            return;
        }

        navigate(to);
    };

    return (
        <PaloChrome
            sections={sections}
            activeSectionKey={activeSectionKey}
            activePath={location.pathname + location.search}
            onNavigate={handleNavigate}
            placeLabel={t('app.navigation.youAreInPortal')}
            topbarActions={
                <Box
                    component="a"
                    href={SHOP_URL}
                    sx={{ fontSize: '12.5px', color: 'inherit', textDecoration: 'none', whiteSpace: 'nowrap' }}
                >
                    {shopLink(toWebshop)}
                </Box>
            }
            crumbNote={
                account?.name ? (
                    <>
                        <Box component="span" sx={{ opacity: 0.5, margin: '0 2px' }}>
                            ·
                        </Box>
                        {account.name}
                        {accounts.length > 1 &&
                            ` · ${intl.formatMessage(
                                { id: 'app.navigation.accountCount' },
                                { count: accounts.length }
                            )}`}
                    </>
                ) : undefined
            }
            userName={fullName}
            userSubline={account?.name}
            userInitials={paloInitials(fullName)}
            userMenuItems={[
                ...(otherAccounts.length > 0
                    ? [
                          { label: t('app.navigation.switchAccount'), heading: true },
                          ...otherAccounts.map(other => ({
                              label: other.name,
                              icon: <StorefrontOutlined />,
                              onClick: () => switchAccount(other.id),
                          })),
                      ]
                    : []),
                {
                    label: t(isImpersonator ? 'app.navigation.exitImpersonator' : 'app.navigation.logout'),
                    href: paloSignOutHref(isImpersonator),
                    icon: <LogoutOutlined />,
                },
            ]}
            logo={
                <Box sx={{ textAlign: 'center' }}>
                    <img src={paloLogo} alt="PALO" style={{ width: 116, height: 'auto', display: 'block' }} />
                    <Box
                        sx={{
                            fontSize: '9.5px',
                            fontWeight: 600,
                            letterSpacing: '.09em',
                            textTransform: 'uppercase',
                            color: '#6C7786',
                            marginTop: '3px',
                        }}
                    >
                        customer
                    </Box>
                </Box>
            }
        >
            {children}
        </PaloChrome>
    );
};
